/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any  */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import { useCreateAsyncExportMutation } from '../data-layer/entReportsApi';
import { entReportsActions } from '../data-store/entReportsSlice';
import { type CreateAsyncExportRequest } from '../types';
import formatEntReportType from '../types/entReportTypes';

/**
 * useExportReport Hook
 *
 * Custom hook for managing entitlement report export operations with progress tracking,
 * banner notifications, and duplicate export prevention. Handles async export creation,
 * status monitoring, and user feedback through banner messages.
 *
 * @hook
 * @returns {Object} Export management utilities including:
 *   - exportReport: Function to initiate async report export
 *   - isExporting: Boolean indicating if export is in progress
 *   - closeBanner: Function to manually close export banners
 *   - isExportDisabled: Function to check if export is disabled for a report type
 *   - activeExports: Array of currently active export operations
 *
 * @example
 * const { exportReport, isExporting, closeBanner } = useExportReport();
 *
 * // Export a report
 * await exportReport({
 *   reportType: 'INHERENT_ENTITLEMENT_RISK',
 *   format: 'EXCEL',
 *   filters: {...}
 * });
 */
const useExportReport = () => {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const [createAsyncExport, { isLoading }] = useCreateAsyncExportMutation();
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inProgressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { activeExports } = useTypedSelector(
    (state: RootState) => state.aag.entReports
  );

  const toTitleCaseReportType = useCallback(
    (rt: string) => formatEntReportType(rt),
    []
  );

  const showSuccessBanner = useCallback(
    (reportType: string) => {
      // Clear all existing timeouts
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      if (inProgressTimeoutRef.current) {
        clearTimeout(inProgressTimeoutRef.current);
        inProgressTimeoutRef.current = null;
      }

      dispatch(
        entReportsActions.setExportBanner({
          isVisible: true,
          title: l('Exporting'),
          description: `${toTitleCaseReportType(reportType)} ${l(
            "export is running in background. You'll receive an email when your report is ready. You can also download it later from My Downloads in your profile."
          )}`,
          colorTheme: 'Information',
          kind: 'low',
        })
      );

      // Auto dismiss after 2 seconds
      successTimeoutRef.current = setTimeout(() => {
        dispatch(entReportsActions.hideExportBanner());
      }, 2000);
    },
    [dispatch, toTitleCaseReportType, l]
  );

  const showErrorBanner = useCallback(
    (reportType: string, errorMessage?: string) => {
      // Clear all existing timeouts
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }

      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }

      if (inProgressTimeoutRef.current) {
        clearTimeout(inProgressTimeoutRef.current);
        inProgressTimeoutRef.current = null;
      }

      // Hide any existing banner first to force re-render
      dispatch(entReportsActions.hideExportBanner());

      // Use setTimeout to ensure the hide action is processed before showing new banner
      setTimeout(() => {
        let errorDescription = '';

        if (errorMessage && errorMessage.trim().length > 0) {
          errorDescription = errorMessage
            .replace(/RISK_DETAILS/g, 'Entitlements with Inherent Risk')
            .replace(
              /INHERENT_ENTITLEMENT_RISK/g,
              l('Entitlements with Inherent Risk')
            )
            .replace(/USER_DETAILS/g, 'Entitlements with Inherent Risk')
            .replace(
              /INHERENT_ENTITLEMENT_USER/g,
              l('Entitlements with Inherent Risk')
            );
        }

        const bannerData = {
          isVisible: true,
          title: l('Export failed'),
          description:
            errorDescription ||
            `${toTitleCaseReportType(reportType)} ${l(
              'export failed. Please try again or contact support if the issue persists.'
            )}`,
          colorTheme: 'Critical' as const,
          kind: 'high' as const,
          exportId: `error-${Date.now()}-${Math.random()}`, // Unique ID to prevent dismissal tracking
        };

        dispatch(entReportsActions.setExportBanner(bannerData));

        // Auto dismiss after 5 seconds
        errorTimeoutRef.current = setTimeout(() => {
          dispatch(entReportsActions.hideExportBanner());
        }, 5000);
      }, 0);
    },
    [dispatch, toTitleCaseReportType, l]
  );

  const closeBanner = useCallback(() => {
    // Clear all timeouts when banner is manually closed
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    if (inProgressTimeoutRef.current) {
      clearTimeout(inProgressTimeoutRef.current);
      inProgressTimeoutRef.current = null;
    }

    dispatch(entReportsActions.hideExportBanner());
  }, [dispatch]);

  const exportReport = useCallback(
    async (payload: CreateAsyncExportRequest) => {
      // Check if this report type is already being exported
      if (
        Array.isArray(activeExports) &&
        activeExports.some((e) => e.reportType === payload.reportType)
      ) {
        // Clear all existing timeouts
        if (inProgressTimeoutRef.current) {
          clearTimeout(inProgressTimeoutRef.current);
          inProgressTimeoutRef.current = null;
        }

        if (errorTimeoutRef.current) {
          clearTimeout(errorTimeoutRef.current);
          errorTimeoutRef.current = null;
        }

        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
          successTimeoutRef.current = null;
        }

        // Show a message that export is already in progress
        dispatch(
          entReportsActions.setExportBanner({
            isVisible: true,
            title: l('Export in Progress'),
            description: `${toTitleCaseReportType(payload.reportType)} ${l(
              'export is already in progress. Please wait for it to complete.'
            )}`,
            colorTheme: 'Information',
            kind: 'low',
          })
        );

        return;
      }

      try {
        // Reset this reportType's progress to 0 so UI starts fresh
        dispatch(
          entReportsActions.setExportProgress({
            reportType: payload.reportType,
            progressPercentage: 0,
          })
        );

        await createAsyncExport(payload).unwrap();

        // Add to active exports for polling
        dispatch(
          entReportsActions.addActiveExport({
            reportType: payload.reportType,
            progress: 0,
          })
        );

        showSuccessBanner(payload.reportType);
      } catch (error) {
        Logger.log('error', 'Entitlement Export failed:', error);

        let errorMessage = '';

        if (error && typeof error === 'object' && 'data' in error) {
          const errorData = error.data as any;

          // For 400 errors: check response.data for server message
          if (errorData?.response?.data) {
            const serverResponse = errorData.response.data;

            if (typeof serverResponse === 'string') {
              errorMessage = serverResponse;
            } else if (serverResponse?.message) {
              errorMessage = serverResponse.message;
            }
          }
          // For 500/network errors: message is directly in data
          else if (errorData?.message) {
            errorMessage = errorData.message;
          }
          // Fallback to string data
          else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        }

        showErrorBanner(payload.reportType, errorMessage);
      }
    },
    [
      createAsyncExport,
      showSuccessBanner,
      showErrorBanner,
      activeExports,
      dispatch,
      toTitleCaseReportType,
      l,
    ]
  );

  const isExportDisabled = useCallback(
    (reportType: string) =>
      Array.isArray(activeExports) &&
      activeExports.some((e) => e.reportType === reportType),
    [activeExports]
  );

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }

      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }

      if (inProgressTimeoutRef.current) {
        clearTimeout(inProgressTimeoutRef.current);
      }
    },
    []
  );

  return {
    exportReport,
    isExporting: isLoading,
    closeBanner,
    isExportDisabled,
    activeExports: Array.isArray(activeExports) ? activeExports : [],
  };
};

export default useExportReport;
